import { updateCalendar, deleteCalendar } from '../../Utils/apiServices';
import { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  InputAdornment,
  Typography,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { getCalendar, createCalendar } from '../../Utils/apiServices';
import ConfirmDialog from '../../Components/ConfirmDialog';

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({
    date: '',
    title: '',
    description: '',
    venue: ''
  });
  const [deleteCalendarConfirmOpen, setDeleteCalendarConfirmOpen] = useState(false);
  const [deleteCalendarLoading, setDeleteCalendarLoading] = useState(false);

  useEffect(() => {
    getCalendar().then((response) => {
      setEvents(response.data.data);
      setFilteredEvents(response.data.data);
    });
  }, []);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleEditOpen = (event) => {
    setSelectedEvent(event);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    setSelectedEvent(null);
    setDeleteCalendarConfirmOpen(false);
  };

  const handleEditSubmit = async () => {
    try {
      const response = await updateCalendar(selectedEvent);
      if (response.data.success) {
        setEvents(events.map(event =>
          event.id === selectedEvent.id ? selectedEvent : event
        ));
        handleEditClose();
      }
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  const handleEditChange = (e) => {
    setSelectedEvent({
      ...selectedEvent,
      [e.target.name]: e.target.value
    });
  };

  const handleChange = (e) => {
    setNewEvent({
      ...newEvent,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async () => {
    try {
      const response = await createCalendar(newEvent);
      setEvents([...events, response.data.data]);
      handleClose();
      setNewEvent({
        date: '',
        title: '',
        description: '',
        venue: ''
      });
    } catch (error) {
      console.error('Error creating event:', error);
    }
  };

  const openDeleteCalendarConfirm = () => {
    if (!selectedEvent?.id) return;
    setDeleteCalendarConfirmOpen(true);
  };

  const closeDeleteCalendarConfirm = () => {
    if (deleteCalendarLoading) return;
    setDeleteCalendarConfirmOpen(false);
  };

  const confirmDeleteCalendar = async () => {
    if (!selectedEvent?.id) return;
    setDeleteCalendarLoading(true);
    try {
      const response = await deleteCalendar(selectedEvent.id);
      if (response.data.success) {
        setEvents(events.filter(event => event.id !== selectedEvent.id));
        setDeleteCalendarConfirmOpen(false);
        handleEditClose();
      }
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setDeleteCalendarLoading(false);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value.toLowerCase();
    setSearchTerm(term);

    const filtered = events.filter(event =>
      event.title.toLowerCase().includes(term) ||
      event.description.toLowerCase().includes(term) ||
      event.venue.toLowerCase().includes(term) ||
      event.date.toLowerCase().includes(term)
    );

    setFilteredEvents(filtered);
  };

  return (
    <Box>
      {/* Colorful page banner */}
      <Box
        sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f97316 0%, #ef4444 50%, #ec4899 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 24px rgba(249,115,22,0.3)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -40,
            right: -40,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.3 }}>📅 Calendar</Typography>
          <Typography variant="body2" sx={{ opacity: 0.85 }}>Schedule and manage upcoming equestrian events</Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 800, lineHeight: 1 }}>{events.length}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Total Events</Typography>
        </Box>
      </Box>

      <ConfirmDialog
        open={deleteCalendarConfirmOpen}
        title="Delete calendar event"
        message="Are you sure you want to delete this event? This action cannot be undone."
        confirmLabel="Delete"
        onCancel={closeDeleteCalendarConfirm}
        onConfirm={confirmDeleteCalendar}
        loading={deleteCalendarLoading}
      />

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
          <TextField
            placeholder="Search by title, venue, description, or date..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={handleSearch}
            fullWidth
            sx={{ maxWidth: { sm: 400 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpen}
            startIcon={<AddIcon />}
            sx={{ flexShrink: 0 }}
          >
            Add Event
          </Button>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
          Showing {filteredEvents.length} of {events.length} events — click a row to edit
        </Typography>
      </Paper>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent>
          <TextField
            name="date"
            label="Date"
            type="date"
            fullWidth
            margin="dense"
            value={newEvent.date}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            name="title"
            label="Title"
            fullWidth
            margin="dense"
            value={newEvent.title}
            onChange={handleChange}
          />
          <TextField
            name="description"
            label="Description"
            fullWidth
            margin="dense"
            multiline
            rows={4}
            value={newEvent.description}
            onChange={handleChange}
          />
          <TextField
            name="venue"
            label="Venue"
            fullWidth
            margin="dense"
            value={newEvent.venue}
            onChange={handleChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={handleEditClose} fullWidth maxWidth="sm">
        <DialogTitle>Edit Event</DialogTitle>
        <DialogContent>
          <TextField
            name="date"
            label="Date"
            type="date"
            fullWidth
            margin="dense"
            value={selectedEvent?.date || ''}
            onChange={handleEditChange}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            name="title"
            label="Title"
            fullWidth
            margin="dense"
            value={selectedEvent?.title || ''}
            onChange={handleEditChange}
          />
          <TextField
            name="description"
            label="Description"
            fullWidth
            margin="dense"
            multiline
            rows={4}
            value={selectedEvent?.description || ''}
            onChange={handleEditChange}
          />
          <TextField
            name="venue"
            label="Venue"
            fullWidth
            margin="dense"
            value={selectedEvent?.venue || ''}
            onChange={handleEditChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={openDeleteCalendarConfirm} variant="outlined" color="error">
            Delete
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow
              sx={{
                '& .MuiTableCell-head': {
                  bgcolor: 'grey.50',
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  letterSpacing: '0.02em',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  py: 1.5,
                },
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Venue</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEvents.map((event) => (
              <TableRow
                key={event.id}
                hover
                onClick={() => handleEditOpen(event)}
                sx={{
                  cursor: 'pointer',
                  '&:last-child td': { borderBottom: 0 },
                  transition: 'background-color 0.15s',
                }}
              >
                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>{event.id}</TableCell>
                <TableCell>
                  <Chip label={event.date} size="small" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.75rem' }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.primary' }}>{event.title}</TableCell>
                <TableCell sx={{ color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {event.description}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary' }}>{event.venue}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Calendar;
